import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import useRealtimeSubscription from '../../hooks/useRealtimeSubscription';
import RoleNavigation from '../../components/ui/RoleNavigation';
import NotificationCenter from '../../components/ui/NotificationCenter';
import DashboardQuickActions from '../../components/ui/DashboardQuickActions';
import MetricsOverview from './components/MetricsOverview';
import CampaignCard from './components/CampaignCard';
import EngagementChart from './components/EngagementChart';
import InfluencerTable from './components/InfluencerTable';
import PendingSubmissions from './components/PendingSubmissions';
import CreateCampaignModal from './components/CreateCampaignModal';
import Button from '../../components/ui/Button';
import BrandPayoutPanel from './components/BrandPayoutPanel';
import RevenueAttributionPanel from './components/RevenueAttributionPanel';
import ROIByCampaignPanel from './components/ROIByCampaignPanel';
import ConversionFunnelPanel from './components/ConversionFunnelPanel';
import AudienceDemographicsPanel from './components/AudienceDemographicsPanel';


const BrandDashboard = () => {
  const { user, userProfile } = useAuth();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [metricsData, setMetricsData] = useState({
    activeCampaigns: 0,
    totalInfluencers: 0,
    avgEngagement: 0,
    totalBudget: 0,
  });

  const [campaignsData, setCampaignsData] = useState([]);
  const [influencersData, setInfluencersData] = useState([]);
  const [pendingSubmissionsData, setPendingSubmissionsData] = useState([]);
  const [chartData, setChartData] = useState([]);

  // Load campaigns for this brand
  const loadCampaigns = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase?.from('campaigns')?.select('id, title, description, status, budget, spent, reward_amount, min_followers, categories, created_at')?.eq('brand_id', user?.id)?.order('created_at', { ascending: false });

      if (!error && data) {
        const mapped = data?.map(c => ({
          id: c?.id,
          name: c?.title,
          description: c?.description || '',
          status: c?.status || 'draft',
          participants: 0,
          engagementRate: 0,
          cashbackRate: parseFloat(c?.reward_amount) || 0,
          conversions: 0,
          budget: parseFloat(c?.budget) || 0,
          spent: parseFloat(c?.spent) || 0,
        }));
        setCampaignsData(mapped);

        // Update metrics
        const active = mapped?.filter(c => c?.status === 'active')?.length;
        const totalBudget = mapped?.reduce((sum, c) => sum + c?.budget, 0);
        setMetricsData(prev => ({
          ...prev,
          activeCampaigns: active,
          totalBudget,
        }));
      }
    } catch (err) {
      console.error('[BrandDashboard] loadCampaigns error:', err);
    }
  }, [user?.id]);

  // Load pending submissions for brand's campaigns
  const loadPendingSubmissions = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase?.from('transactions')?.select(`
          id, amount, purchase_value, status, created_at, social_post_url, receipt_url,
          campaigns(title),
          influencer:user_profiles!transactions_influencer_id_fkey(id, full_name, email)
        `)?.eq('brand_id', user?.id)?.eq('status', 'pending')?.order('created_at', { ascending: false })?.limit(10);

      if (!error && data) {
        const mapped = data?.map(t => ({
          id: t?.id,
          influencerName: t?.influencer?.full_name || t?.influencer?.email || 'Influencer',
          influencerHandle: t?.influencer?.email?.split('@')?.[0] || 'user',
          influencerAvatar: '',
          influencerAvatarAlt: `Profile photo of ${t?.influencer?.full_name || 'influencer'}`,
          influencerScore: 0,
          purchaseAmount: parseFloat(t?.purchase_value) || 0,
          campaign: t?.campaigns?.title || 'Campaign',
          postEngagement: 0,
          receiptUrl: t?.receipt_url || '',
          postUrl: t?.social_post_url || '',
          submittedAt: new Date(t.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        }));
        setPendingSubmissionsData(mapped);
        setMetricsData(prev => ({ ...prev, totalInfluencers: mapped?.length }));
      }
    } catch (err) {
      console.error('[BrandDashboard] loadPendingSubmissions error:', err);
    }
  }, [user?.id]);

  // Load influencer stats from approved transactions
  const loadInfluencers = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase?.from('transactions')?.select(`
          id, amount, status,
          influencer:user_profiles!transactions_influencer_id_fkey(id, full_name, email),
          scores!inner(total_score)
        `)?.eq('brand_id', user?.id)?.eq('status', 'approved')?.limit(5);

      if (!error && data) {
        const influencerMap = {};
        data?.forEach(t => {
          const inf = t?.influencer;
          if (!inf) return;
          if (!influencerMap?.[inf?.id]) {
            influencerMap[inf.id] = {
              id: inf?.id,
              name: inf?.full_name || inf?.email || 'Influencer',
              handle: inf?.email?.split('@')?.[0] || 'user',
              avatar: '',
              avatarAlt: `Profile photo of ${inf?.full_name || 'influencer'}`,
              score: t?.scores?.total_score || 0,
              followers: 0,
              engagement: 0,
              conversions: 0,
            };
          }
          influencerMap[inf.id].conversions += 1;
        });
        setInfluencersData(Object.values(influencerMap));
      }
    } catch (err) {
      console.error('[BrandDashboard] loadInfluencers error:', err);
    }
  }, [user?.id]);

  // Build chart data from transactions grouped by month
  const loadChartData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase?.from('transactions')?.select('created_at, status, amount')?.eq('brand_id', user?.id)?.order('created_at', { ascending: true });

      if (!error && data) {
        const monthMap = {};
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        data?.forEach(t => {
          const d = new Date(t.created_at);
          const key = months?.[d?.getMonth()];
          if (!monthMap?.[key]) monthMap[key] = { month: key, engagement: 0, conversions: 0 };
          monthMap[key].conversions += 1;
          if (t?.status === 'approved') monthMap[key].engagement += parseFloat(t?.amount) || 0;
        });
        const chart = Object.values(monthMap)?.slice(-6);
        setChartData(chart?.length > 0 ? chart : [
          { month: 'Jan', engagement: 0, conversions: 0 },
        ]);
      }
    } catch (err) {
      console.error('[BrandDashboard] loadChartData error:', err);
    }
  }, [user?.id]);

  // Initial load
  useEffect(() => {
    if (!user?.id) return;
    const init = async () => {
      setLoading(true);
      await Promise.all([
        loadCampaigns(),
        loadPendingSubmissions(),
        loadInfluencers(),
        loadChartData(),
      ]);
      setLoading(false);
    };
    init();
  }, [user?.id]);

  // Real-time: campaigns
  useRealtimeSubscription({
    channelName: `brand-campaigns-${user?.id}`,
    table: 'campaigns',
    filter: user?.id ? `brand_id=eq.${user?.id}` : undefined,
    enabled: !!user?.id,
    onChange: () => loadCampaigns(),
  });

  // Real-time: transactions (pending submissions queue)
  useRealtimeSubscription({
    channelName: `brand-transactions-${user?.id}`,
    table: 'transactions',
    filter: user?.id ? `brand_id=eq.${user?.id}` : undefined,
    enabled: !!user?.id,
    onChange: () => {
      loadPendingSubmissions();
      loadInfluencers();
      loadChartData();
    },
  });

  const handleEditCampaign = (campaignId) => {
    console.log('Edit campaign:', campaignId);
  };

  const handlePauseCampaign = (campaignId) => {
    console.log('Pause/Resume campaign:', campaignId);
  };

  const handleViewDetails = (campaignId) => {
    console.log('View campaign details:', campaignId);
  };

  const handleApproveSubmission = async (submissionId) => {
    try {
      const { error } = await supabase?.from('transactions')?.update({ status: 'approved', reviewed_by: user?.id, reviewed_at: new Date()?.toISOString() })?.eq('id', submissionId);
      if (!error) {
        setPendingSubmissionsData(prev => prev?.filter(s => s?.id !== submissionId));
      }
    } catch (err) {
      console.error('[BrandDashboard] handleApproveSubmission error:', err);
    }
  };

  const handleRejectSubmission = async (submissionId) => {
    try {
      const { error } = await supabase?.from('transactions')?.update({ status: 'rejected', reviewed_by: user?.id, reviewed_at: new Date()?.toISOString() })?.eq('id', submissionId);
      if (!error) {
        setPendingSubmissionsData(prev => prev?.filter(s => s?.id !== submissionId));
      }
    } catch (err) {
      console.error('[BrandDashboard] handleRejectSubmission error:', err);
    }
  };

  const handleCreateCampaign = async (campaignData) => {
    if (!user?.id) return;
    try {
      const { error } = await supabase?.from('campaigns')?.insert({
          brand_id: user?.id,
          title: campaignData?.name || campaignData?.title,
          description: campaignData?.description || '',
          budget: parseFloat(campaignData?.budget) || 0,
          reward_amount: parseFloat(campaignData?.rewardAmount || campaignData?.cashbackRate) || 0,
          status: 'draft',
        });
      if (!error) {
        setIsCreateModalOpen(false);
        loadCampaigns();
      }
    } catch (err) {
      console.error('[BrandDashboard] handleCreateCampaign error:', err);
    }
  };

  const displayName = userProfile?.full_name || user?.user_metadata?.full_name || 'Brand Partner';

  return (
    <div className="min-h-screen bg-background">
      <RoleNavigation role="brand" userName={displayName} />
      <div className="pt-[60px]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                Brand Dashboard
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Manage campaigns, track performance, and engage with influencers
              </p>
            </div>
            <div className="flex items-center gap-3">
              <NotificationCenter role="brand" />
              <Button
                variant="default"
                iconName="Plus"
                onClick={() => setIsCreateModalOpen(true)}>
                Create Campaign
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-6 md:space-y-8">
              <MetricsOverview metrics={metricsData} />

              <EngagementChart data={chartData} />

              <div>
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground">
                    Active Campaigns
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Filter">
                    Filter
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-4 md:gap-6">
                  {campaignsData?.map((campaign) =>
                    <CampaignCard
                      key={campaign?.id}
                      campaign={campaign}
                      onEdit={handleEditCampaign}
                      onPause={handlePauseCampaign}
                      onViewDetails={handleViewDetails} />
                  )}
                  {campaignsData?.length === 0 && (
                    <div className="bg-card rounded-xl border border-border p-12 text-center">
                      <p className="text-muted-foreground">No campaigns yet. Create your first campaign!</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                <div className="lg:col-span-2">
                  <InfluencerTable influencers={influencersData} />
                </div>
                <div>
                  <PendingSubmissions
                    submissions={pendingSubmissionsData}
                    onApprove={handleApproveSubmission}
                    onReject={handleRejectSubmission} />
                </div>
              </div>

              <BrandPayoutPanel brandId={user?.id} />

              {/* Analytics Section */}
              <div>
                <div className="mb-4 md:mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground">Campaign Analytics</h2>
                  <p className="text-sm text-muted-foreground mt-1">Revenue attribution, ROI, conversion funnels, and audience insights</p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                  <RevenueAttributionPanel brandId={user?.id} />
                  <ROIByCampaignPanel brandId={user?.id} />
                  <ConversionFunnelPanel brandId={user?.id} />
                  <AudienceDemographicsPanel brandId={user?.id} />
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6">
                  Quick Actions
                </h2>
                <DashboardQuickActions role="brand" />
              </div>
            </div>
          )}
        </div>
      </div>
      <CreateCampaignModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateCampaign} />
    </div>
  );
};

export default BrandDashboard;